import styled from 'styled-components'
import PropTypes from 'prop-types'
import { useQuery } from 'react-query'
import { t } from 'ttag'
import { useContext, useState } from 'react'

import { Text } from '@h2/components/typography/text'
import { PB97T, BLACK } from '@h2/components/colors'
import { GravatarIcon } from '@h2/common/components/gravatarIcon/index.js'
import { Flex } from '@h2/components/layout/flex'
import { api } from '@h2/api'
import { Skeleton } from '@h2/components/loading/skeleton'
import { Button } from '@h2/components/buttons/button'
import { Modal } from '@h2/components/containers/modal'
import { Heading } from '@h2/components/typography/heading'
import { features } from '@h2/components/media'
import { Globals } from '@h2/globals'
import { VIEW_MODE_MANAGEMENT } from '@h2/common/constants'
import { ProgressBar } from '@h2/components/display/progressBar'
import {
  CertificationBadgeIcon,
  MajorBadgeIcon,
  OccupationBadgeIcon,
  ProgramBadgeIcon,
  CheckCircleIcon,
  MinusCircleIcon
} from '@h2/components/icons'
import { Box } from '@h2/components/layout/box'

import { ReferEducator } from './panels/referEducator'
import { ProfessionalDevelopment } from './panels/professionalDevelopment'
import { AddExplorers } from './panels/addExplorers'
import { AccountPortability } from './panels/accountPortability'
import { NavigatorConnection } from './panels/navigatorConnection'
import { SandiProfessionalDevelopment } from './panels/nevadaSpecific/sandiProfessionalDevelopment'

const Wrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  padding: 15px;
  border-radius: 5px;
  background-color: ${PB97T};
  flex-grow: 1;
  margin-bottom: 20px;
  @media (${features.minWidth.desktop}) {
    max-width: ${({ isInFlex }) => (isInFlex ? 'calc(50% - 50px)' : '100%')};
  }
`
const ProgressList = styled.ul`
  padding: 0;
  list-style: none;
  margin: 0;
`
const ProgressListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;

  > button {
    padding: 0;
  }
`
const NoWrapText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const panels = {
  referNavigator: ReferEducator,
  professionalDevelopment: ProfessionalDevelopment,
  explorerConnection: AddExplorers,
  accountPortability: AccountPortability,
  navigatorConnection: NavigatorConnection,
  precisionNavigation: SandiProfessionalDevelopment
}
const BadgeGraphic = ({ badge }) => {
  const findIcon = badgeId => {
    switch (badgeId) {
      case 'program':
        return <ProgramBadgeIcon size="large" />
      case 'onet':
        return <OccupationBadgeIcon size="large" />
      case 'certification_profile':
        return <CertificationBadgeIcon size="large" />
      case 'major':
        return <MajorBadgeIcon size="large" />
    }
  }

  return <div title={badge.badgeType.name}>{findIcon(badge.badgeType.id)}</div>
}
const Account = ({ ...props }) => {
  const { userProfile: user, viewMode, isSubscriber } = useContext(Globals)
  const [actionDetailModalId, setActionDetailModalId] = useState(false)
  const { data, isLoading } = useQuery(['account-status', user, viewMode], () => {
    return api.fetch('/user/account/progress', {
      method: 'POST',
      body: JSON.stringify({ navigatorView: viewMode === VIEW_MODE_MANAGEMENT })
    })
  })
  const { data: userBadge, isLoading: badgesLoading } = useQuery(['badges', user], () => {
    return api.fetch('/badging/badges', {
      method: 'GET'
    })
  })
  const progressList = () => {
    if (isLoading) {
      return <Skeleton width="100%" height="100px" />
    }

    return (
      <ProgressList>
        {data?.tasks.map(task => (
          <ProgressListItem key={`task-${task.taskId}`}>
            {task.completed ? (
              <CheckCircleIcon size="small" />
            ) : (
              <MinusCircleIcon size="small" />
            )}{' '}
            <Button
              variant="link"
              size="medium"
              onClick={() => setActionDetailModalId(task)}>
              <Text as="span">{task.name}</Text>
            </Button>
          </ProgressListItem>
        ))}
      </ProgressList>
    )
  }
  const guessType = ({
    hasAdmin,
    hasSchoolAdmin,
    hasManager,
    isNavigator,
    isPremium,
    isRoot
  }) => {
    return isRoot
      ? `Root User`
      : hasAdmin
        ? t`Administrator`
        : hasSchoolAdmin
          ? t`School Admin`
          : !isSubscriber && isNavigator
            ? isPremium
              ? 'Premium Navigator'
              : 'Free Navigator'
            : hasManager
              ? t`Manager`
              : t`Explorer`
  }
  const panelContent = panelId => {
    if (!panelId) {
      return panelId
    }

    const PanelComponent = panels[panelId]

    if (PanelComponent) {
      return <PanelComponent task={actionDetailModalId} />
    }

    return panelId
  }
  const userFullName = () => {
    if (!user) {
      return 'n/a'
    }

    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`
    }

    return user.userDisplayName
  }

  return (
    <Wrap {...props}>
      <Flex direction="column" gap="15px" style={{ overflow: 'hidden', width: '100%' }}>
        <Flex alignItems="center">
          <GravatarIcon width="55px" height="55px" />
          <div>
            <NoWrapText size="large" margin="0 0 0 12px" weight="bold" color={BLACK}>
              {userFullName()}
            </NoWrapText>
            <NoWrapText margin="0 0 0 12px">
              <Text as="span" weight="bold">{t`Account Type: `}</Text>
              {guessType(user)}
            </NoWrapText>
          </div>
        </Flex>
        <Box padding="5px">
          <ProgressBar value={data?.percentComplete} />
        </Box>
        <div>{progressList()}</div>
        {badgesLoading ? (
          <Skeleton />
        ) : (
          <div>
            {userBadge?.badges.map(badge => (
              <BadgeGraphic key={badge.badgeType.id} badge={badge} />
            ))}
          </div>
        )}
      </Flex>
      <Modal
        isOpen={Boolean(actionDetailModalId)}
        closeOnClickOverlay={true}
        onClose={() => setActionDetailModalId(false)}>
        <Modal.Header>
          <Heading level="2">{actionDetailModalId?.name ?? 'none'}</Heading>
        </Modal.Header>
        <Modal.Body>{panelContent(actionDetailModalId?.taskId) || 'none'}</Modal.Body>
      </Modal>
    </Wrap>
  )
}

Account.propTypes = {
  fullWidth: PropTypes.bool
}

export { Account }
